// src/pages/gt3x/Briefing.jsx
import { useEffect, useMemo, useState } from "react";
import { fetchSheetViaProxy } from "../../lib/sheets";
import RaceSessionSelect from "../../components/RaceSessionSelect";
import { getAutoRaceFromSchedule, normalizeSheetMatrix } from "../../lib/autoRaceFromSchedule";

const RANGE = "Briefings!A1:AI100";
const SCHEDULE_RANGE = "Schedule!A1:D";

// kleine Hilfsfunktion: Text/Zelle rendern, URLs als Link
function renderCell(cell) {
    if (cell == null) return "";
    const str = String(cell).trim();
    if (!str) return "";

    // URL?
    if (/^https?:\/\//i.test(str)) {
        // Bild-URL? (png/jpg/jpeg/gif/webp/svg)
        if (/\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(str)) {
            return (
                <a href={str} target="_blank" rel="noreferrer">
                    <img
                        src={str}
                        alt=""
                        style={{ maxWidth: "100%", height: "auto", display: "block" }}
                    />
                </a>
            );
        }

        // normale URL → Textlink
        return (
            <a href={str} target="_blank" rel="noreferrer">
                {str}
            </a>
        );
    }

    return str;
}

// Block-Helfer: Zeilenbereich aus Matrix holen (2 Spalten)
function sliceBlock(matrix, colStart, firstRow, lastRow) {
    const col2 = colStart + 1;
    const out = [];

    for (let r = firstRow; r <= lastRow; r++) {
        const row = matrix[r];
        if (!row) continue;

        const left = row[colStart] ?? "";
        const right = row[col2] ?? "";

        if (
            (left && String(left).trim() !== "") ||
            (right && String(right).trim() !== "")
        ) {
            out.push([left, right]);
        }
    }
    return out;
}

function BriefingTables({ data }) {
    const everett = { fontFamily: "var(--ff-Everett), sans-serif" };

    if (!data) {
        return (
            <div className="card overview-card">
                <p className="muted">
                    Für dieses Rennen sind aktuell noch keine Briefing-Daten hinterlegt.
                </p>
            </div>
        );
    }

    const {
        sessionRows,
        trackRows,
        detailRows,
        weather: { img1Left, img1Right, tempImg, cloudsImg },
        startzone: { imgStartzone1, imgStartzone2 },
    } = data;

    // Debug-Ausgabe, damit wir sehen, ob rechte Spalte wirklich leer ist
    console.log("Briefing sessionRows:", sessionRows);
    console.log("Briefing trackRows:", trackRows);
    console.log("Briefing detailRows:", detailRows);

    return (
        <div className="card overview-card">
            {/* 1. Session – inGame Zeiten */}
            <section className="briefing-section" style={{ marginBottom: 24 }}>
                <div className="fancyTable">
                    <table className="tbl tbl-overview-table overview-table tbl-briefing">
                        <thead>
                        <tr>
                            <th className="align-left">Session</th>
                            <th className="align-left">inGame Zeiten</th>
                        </tr>
                        </thead>
                        <tbody style={everett}>
                        {sessionRows.map(([left, right], idx) => (
                            <tr key={idx}>
                                <td className="align-left">{renderCell(left)}</td>
                                <td className="align-left">{renderCell(right)}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* 2. Track State */}
            <section className="briefing-section" style={{ marginBottom: 24 }}>
                <div className="fancyTable">
                    <table className="tbl tbl-overview-table overview-table tbl-briefing">
                        <thead>
                        <tr>
                            <th className="align-left">Trackstate</th>
                            <th className="align-left">Details</th>
                        </tr>
                        </thead>
                        <tbody style={everett}>
                        {trackRows.map(([left, right], idx) => (
                            <tr key={idx}>
                                <td className="align-left">{renderCell(left)}</td>
                                <td className="align-left">{renderCell(right)}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* 3. Wetter (aus GT-Sprint Serie) */}
            <section className="briefing-section" style={{ marginBottom: 24 }}>
                <div className="fancyTable">
                    <table className="tbl tbl-overview-table overview-table tbl-briefing">
                        <thead>
                        <tr>
                            <th className="align-left">Wetter (aus GT-Sprint Serie)</th>
                            <th className="align-left">Details</th>
                        </tr>
                        </thead>
                        <tbody>
                        {/* Zeile 1: zwei Karten nebeneinander */}
                        <tr>
                            <td className="weather-img-cell weather-img-cell--narrow">
                                {img1Left && <img src={img1Left} alt="Weather map 1" />}
                            </td>
                            <td className="weather-img-cell">
                                {img1Right && <img src={img1Right} alt="Weather map 2" />}
                            </td>
                        </tr>

                        {/* Forecast Temperature */}
                        <tr>
                            <td colSpan={2} className="weather-subtitle">
                                Forecast Temperature
                            </td>
                        </tr>
                        <tr>
                            <td colSpan={2} className="weather-img-row">
                                {tempImg && (
                                    <img src={tempImg} alt="Forecast temperature" />
                                )}
                            </td>
                        </tr>

                        {/* Clouds & Percipitation */}
                        <tr>
                            <td colSpan={2} className="weather-subtitle">
                                Forecast Clouds &amp; Percipitation
                            </td>
                        </tr>
                        <tr>
                            <td colSpan={2} className="weather-img-row">
                                {cloudsImg && (
                                    <img src={cloudsImg} alt="Clouds & precipitation" />
                                )}
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>
            </section>

            {/* 4. Startzone */}
            <section className="briefing-section" style={{ marginBottom: 24 }}>
                <div className="fancyTable">
                    <table className="tbl tbl-overview-table overview-table tbl-briefing">
                        <thead>
                        <tr>
                            <th className="align-left">Startzone</th>
                            <th className="align-left">Details</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                            <td colSpan={2} className="weather-img-row">
                                {imgStartzone1 && (
                                    <a href={imgStartzone1} target="_blank" rel="noreferrer">
                                        <img src={imgStartzone1} alt="Startzone 1" />
                                    </a>
                                )}
                            </td>
                        </tr>

                        <tr>
                            <td colSpan={2} className="weather-img-row">
                                {imgStartzone2 && (
                                    <a href={imgStartzone2} target="_blank" rel="noreferrer">
                                        <img src={imgStartzone2} alt="Startzone 2" />
                                    </a>
                                )}
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>
            </section>

            {/* 5. Weitere Details */}
            <section className="briefing-section">
                <div className="fancyTable">
                    <table className="tbl tbl-overview-table overview-table tbl-briefing">
                        <thead>
                        <tr>
                            <th className="align-left">Weitere Details</th>
                            <th className="align-left">Details</th>
                        </tr>
                        </thead>
                        <tbody style={everett}>
                        {detailRows.map(([left, right], idx) => (
                            <tr key={idx}>
                                <td className="align-left">{renderCell(left)}</td>
                                <td className="align-left">{renderCell(right)}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
}

export default function Briefing() {
    // ✅ race wird automatisch gesetzt (Schedule), kann aber manuell überschrieben werden
    const [race, setRace] = useState(null);
    const [raceTouched, setRaceTouched] = useState(false);

    const [matrix, setMatrix] = useState([]); // komplette Matrix A1..AI als 2D-Array
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    // ======================================================
    // 1) AUTO-DEFAULT: Schedule laden -> aktives Rennen bestimmen
    // ======================================================
    useEffect(() => {
        (async () => {
            try {
                const auto = await getAutoRaceFromSchedule(fetchSheetViaProxy, SCHEDULE_RANGE, "briefing");
                if (!raceTouched) setRace(auto);
            } catch (e) {
                console.warn("Schedule auto-race failed (briefing)", e);
                if (!raceTouched) setRace(1);
            }
        })();
    }, [raceTouched]);


    // ======================================================
    // 2) INHALTE LADEN: Briefings-Matrix laden (bleibt wie gehabt)
    // ======================================================
    useEffect(() => {
        (async () => {
            setLoading(true);
            setErr("");
            try {
                const res = await fetchSheetViaProxy({ range: RANGE });
                const m = normalizeSheetMatrix(res);
                setMatrix(m);
            } catch (e) {
                console.error("Briefing load error:", e);
                setErr("Fehler beim Laden der Briefing-Daten.");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    // ======================================================
    // 3) Datenblöcke für ausgewähltes Rennen bauen
    // ======================================================
    const dataForRace = useMemo(() => {
        if (!Array.isArray(matrix) || matrix.length === 0) return null;
        if (!race || race < 1) return null;

        const colStart = (race - 1) * 3;

        const sessionRows = sliceBlock(matrix, colStart, 2, 5);
        const trackRows = sliceBlock(matrix, colStart, 8, 9);

        const urlCol = colStart;
        const img1Left = matrix[11]?.[urlCol] ?? "";
        const img1Right = matrix[12]?.[urlCol] ?? "";
        const tempImg = matrix[13]?.[urlCol] ?? "";
        const cloudsImg = matrix[14]?.[urlCol] ?? "";
        const imgStartzone1 = matrix[21]?.[urlCol] ?? "";
        const imgStartzone2 = matrix[22]?.[urlCol] ?? "";

        const detailRows = sliceBlock(matrix, colStart, 16, 20);

        const hasContent =
            sessionRows.length ||
            trackRows.length ||
            detailRows.length ||
            img1Left ||
            img1Right ||
            tempImg ||
            cloudsImg ||
            imgStartzone1 ||
            imgStartzone2;

        if (!hasContent) return null;

        return {
            sessionRows,
            trackRows,
            detailRows,
            weather: { img1Left, img1Right, tempImg, cloudsImg },
            startzone: { imgStartzone1, imgStartzone2 },
        };
    }, [matrix, race]);

    return (
        <div className="rv-page">
            <section className="rv-page-hero">
                <div className="rv-page-hero__inner">
                    <div>
                        <div className="rv-page-kicker">GT3 Xperience</div>
                        <h1 className="rv-page-title">Briefing</h1>
                        <p className="rv-page-subtitle">
                            Aktuelle Renninformationen, Session-Zeiten, Trackstate, Wetter und Startzone.
                        </p>
                    </div>
                </div>
            </section>

            <RaceSessionSelect
                valueRace={race}
                onRaceChange={setRace}
                onUserRaceChange={() => setRaceTouched(true)}
                autoSelectFirst={false}
                showSession={false}
            />
            {err && (
                <div className="card overview-card">
                    <p className="muted">{err}</p>
                </div>
            )}

            {loading && (
                <div className="card overview-card">
                    <p>Lade Briefing-Daten …</p>
                </div>
            )}

            {!loading && !err && <BriefingTables data={dataForRace} />}
        </div>
    );
}
